import { useEffect } from "react";
import { useAuthStore } from "../store/useAuthStore";
import axiosClient from "../lib/axiosClient";

/** Fallback when the login response carried no idle timeout (matches the DB default). */
const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
];

/**
 * useIdleTimeout
 *
 * Client-side enforcement of the idle timeout configured in the auth_settings DB table
 * (served at login as settings.sessionIdleTimeoutInMinutes). After that long with no user
 * interaction, the hook notifies the server (so the sign-out is audited as SESSION_EXPIRED),
 * clears the session, and opens the session-expired dialog for re-login.
 *
 * Activity resets are throttled to once a second so mousemove doesn't churn timers.
 */
export function useIdleTimeout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const idleTimeoutMinutes =
    useAuthStore((s) => s.user?.settings?.sessionIdleTimeoutInMinutes) ??
    DEFAULT_IDLE_TIMEOUT_MINUTES;

  useEffect(() => {
    if (!isAuthenticated) return;

    let timer: ReturnType<typeof setTimeout>;
    let lastReset = 0;

    const expire = () => {
      // Capture the token NOW and attach it explicitly: axios request interceptors run in a
      // microtask, i.e. AFTER the synchronous logout() below has wiped the store — relying on
      // the interceptor would send this audit request with no Authorization header and the
      // session-activity log would show "Unknown user".
      const { accessToken } = useAuthStore.getState();
      axiosClient
        .post(
          "/v1/auth/logout",
          { reason: "IDLE_TIMEOUT" },
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined
        )
        .catch(() => {});
      // Order matters below too: logout() resets the whole store, so the sessionExpired flag
      // must be set afterwards.
      const { logout, setSessionExpired } = useAuthStore.getState();
      logout();
      setSessionExpired(
        true,
        `Your session ended due to inactivity. Please log in again to continue.`
      );
    };

    const reset = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return;
      lastReset = now;
      clearTimeout(timer);
      timer = setTimeout(expire, idleTimeoutMinutes * 60_000);
    };

    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, reset, { passive: true })
    );
    // Start the clock immediately (lastReset=0 lets this first call through the throttle).
    reset();

    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [isAuthenticated, idleTimeoutMinutes]);
}
